import { dateObjectToServerDay } from '@/helpers/dates'
import { formatDollars } from '@/helpers/money'

const MONTHS = 12
const DAY_MS = 24 * 60 * 60 * 1000

const SHORT_MONTH = new Intl.DateTimeFormat('en-US', { month: 'short' })
const LONG_MONTH = new Intl.DateTimeFormat('en-US', { month: 'long' })
const LONG_MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' })

const pad = value => String(value).padStart(2, '0')
const monthKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
const noonOf = date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12)
const noonOfDay = day => {
  const [year, month, date] = day.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, date, 12)
}
const daysBetween = (from, to) => Math.round((to - from) / DAY_MS)

export function lastTwelveMonths(now = new Date()) {
  const first = new Date(now.getFullYear(), now.getMonth() - (MONTHS - 1), 1)
  const months = Array.from({ length: MONTHS }, (_, index) => {
    const date = new Date(first.getFullYear(), first.getMonth() + index, 1)
    return { key: monthKey(date), date }
  })
  return {
    range: { beginning_date: dateObjectToServerDay(first), ending_date: dateObjectToServerDay(now) },
    months
  }
}

export function monthlyBars(periods, months) {
  const grossByMonth = new Map(periods.map(period => [period.key, period.gross]))
  const max = Math.max(0, ...months.map(month => grossByMonth.get(month.key) ?? 0))
  return months.map((month, index) => {
    const gross = grossByMonth.get(month.key) ?? 0
    return {
      key: month.key,
      date: month.date,
      label: SHORT_MONTH.format(month.date),
      title: `${MONTH_YEAR.format(month.date)}: ${formatDollars(gross)}`,
      gross,
      height: max > 0 ? Math.round((gross / max) * 1000) / 10 : 0,
      current: index === months.length - 1
    }
  })
}

export function barsDescription(bars) {
  const sold = bars.filter(bar => bar.gross > 0)
  if (sold.length === 0) return 'Monthly gross revenue: no sales in the last 12 months'
  return `Monthly gross revenue: ${sold.map(bar => bar.title).join(', ')}`
}

export function netSinceLabel(bars, now = new Date()) {
  const first = bars.findIndex(bar => bar.gross > 0)
  if (first <= 0) return 'net'
  const { date } = bars[first]
  const month = date.getFullYear() === now.getFullYear() ? LONG_MONTH.format(date) : LONG_MONTH_YEAR.format(date)
  return `net, since ${month}`
}

export function orderCadence(rows, now = new Date()) {
  const days = [
    ...new Set(rows.filter(row => Number(row?.attributes?.orders) > 0).map(row => row.attributes.day.slice(0, 10)))
  ].sort()
  if (days.length === 0) return null
  const first = noonOfDay(days[0])
  const last = noonOfDay(days[days.length - 1])
  return {
    everyDays: days.length > 1 ? daysBetween(first, last) / (days.length - 1) : null,
    daysSinceLast: Math.max(0, daysBetween(last, noonOf(now)))
  }
}

export function cadenceInterval(everyDays) {
  const days = Math.max(1, Math.round(everyDays))
  if (days === 1) return 'every day'
  if (days < 14) return `every ${days} days`
  if (days < 60) return `every ${Math.round(days / 7)} weeks`
  return `every ${Math.round(days / 30)} months`
}

export function lastOrderAgo(daysSinceLast) {
  if (daysSinceLast === 0) return 'today'
  if (daysSinceLast === 1) return 'yesterday'
  return `${daysSinceLast} days ago`
}
